import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { Palette, Code, Star } from 'lucide-react';

function AnimatedCounter({ end, suffix = '', duration = 2 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const endNum = parseInt(end);
    const step = endNum / (duration * 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= endNum) {
        setCount(endNum);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [isInView, end, duration]);

  return (
    <span ref={ref} className="stat-number font-heading text-3xl md:text-4xl font-light">
      {count}{suffix}
    </span>
  );
}

const skills = [
  { icon: Palette, label: 'Custom Design' },
  { icon: Code, label: 'Modern Tech' },
  { icon: Star, label: 'Premium Quality' },
];

export default function AboutSection() {
  return (
    <section
      id="about"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ backgroundColor: 'var(--bg-base)' }}
      data-testid="about-section"
    >
      {/* Decorative blob */}
      <div className="absolute top-1/3 right-0 w-96 h-96 blob-pink float-anim opacity-25 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-1 gap-12 md:gap-20 items-center">
          {/* Image */}

          {/* Text Content */}
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <p
              className="font-body text-xs font-semibold uppercase tracking-[0.2em]"
              style={{ color: 'var(--text-accent)' }}
            >
              The Designer
            </p>

            <h2
              className="font-heading text-4xl md:text-5xl font-light tracking-tight leading-tight"
              style={{ color: 'var(--text-primary)' }}
              data-testid="about-heading"
            >
              Where code meets
              <br />
              <span className="italic" style={{ color: 'var(--primary)' }}>celebration</span>
            </h2>

            <div className="space-y-4">
              <p
                className="font-body text-base md:text-lg font-normal leading-relaxed"
                style={{ color: 'var(--text-secondary)' }}
              >
                As a software engineer with a passion for design, I specialize
                in creating bespoke digital invitation experiences. Every website
                I build is a unique reflection of the celebration it represents
                &mdash; thoughtfully crafted with modern web technologies and an
                eye for timeless elegance.
              </p>
              <p
                className="font-body text-base md:text-lg font-normal leading-relaxed"
                style={{ color: 'var(--text-secondary)' }}
              >
                From responsive layouts to smooth animations, RSVP management to
                countdown timers &mdash; I bring together the technical and the
                beautiful to make your special day truly memorable online.
              </p>
            </div>

            <motion.div
              className="grid grid-cols-3 gap-6 pt-8 border-t"
              style={{ borderColor: 'var(--border-default)' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.5 }}
            >
              <div>
                <AnimatedCounter end="50" suffix="+" />
                <p className="font-body text-xs uppercase tracking-[0.1em] mt-1" style={{ color: 'var(--text-accent)' }}>
                  Projects
                </p>
              </div>
              <div>
                <AnimatedCounter end="3" suffix="+" duration={1} />
                <p className="font-body text-xs uppercase tracking-[0.1em] mt-1" style={{ color: 'var(--text-accent)' }}>
                  Years Exp
                </p>
              </div>
              <div>
                <AnimatedCounter end="100" suffix="%" />
                <p className="font-body text-xs uppercase tracking-[0.1em] mt-1" style={{ color: 'var(--text-accent)' }}>
                  Happy Clients
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
